import { useEffect } from 'react';
import { Wordmark } from '../../components/Wordmark.jsx';
import { APP_DOWNLOAD_URL } from '../../config/index.js';
import { WaitlistForm } from '../waitlist/WaitlistForm.jsx';
import { brandPlane } from '../../theme/tokens.js';

/**
 * Everything both landing pages have in common: the brand-green field, the
 * wordmark, and the waitlist form along the bottom.
 *
 * There are two pages because there are two pitches — one person deciding, and
 * a table full of people failing to. Either can be marketed on its own, and
 * whichever someone lands on, the number they leave goes to the same place.
 * `source` is the only thing that differs, and it is what makes the split
 * measurable afterwards rather than just a design exercise: it is stored on the
 * row, so "which page did this number come from" is a `GROUP BY` and not a
 * guess.
 *
 * The chrome lives here rather than being copied into both, because the moment
 * it is copied the two start drifting and the second one starts looking like a
 * different company's page.
 *
 * @param {object} props
 * @param {string} props.source  Recorded against every signup from this page.
 * @param {React.ReactNode} props.children  The page's one moving part.
 */
export function LandingShell({ source, children }) {
  // The document behind the shell is cream for the app proper, and has to be
  // brand green here: it is what iOS paints under the status bar and the home
  // indicator, and in the rubber-band at either end of a scroll, so cream there
  // shows up as a margin across the top and bottom of a full-bleed page. See
  // `body.landing-page` in styles/global.css.
  useEffect(() => {
    document.body.classList.add('landing-page');
    return () => document.body.classList.remove('landing-page');
  }, []);

  return (
    <div className="landing-scroll">
      <div style={styles.stage} className="landing-stage grain">
        <div style={styles.column}>
          <header style={styles.head} className="fade-in">
            <Wordmark height={26} variant="light" />
          </header>

          <main style={styles.main}>{children}</main>

          <footer style={styles.foot}>
            <WaitlistForm source={source} tone="onBrand" />

            <p style={styles.legal}>
              {APP_DOWNLOAD_URL && (
                <>
                  <a href={APP_DOWNLOAD_URL} style={styles.legalLink}>
                    Get the app
                  </a>
                  <span aria-hidden="true" style={styles.dot}>
                    ·
                  </span>
                </>
              )}
              <a href="/legal/privacy.html" style={styles.legalLink}>
                Privacy
              </a>
              <span aria-hidden="true" style={styles.dot}>
                ·
              </span>
              <a href="/legal/terms.html" style={styles.legalLink}>
                Terms
              </a>
            </p>
          </footer>
        </div>
      </div>
    </div>
  );
}

const styles = {
  stage: {
    // One radial lift over the flat brand colour: enough that the field reads
    // as lit rather than as a swatch, not enough that anyone would call it a
    // gradient.
    background: brandPlane,
    color: '#F7F4ED',
    display: 'flex',
    flexDirection: 'column',
    // Centres the column horizontally. Was `justifyContent` on a row, which
    // also stretched the column to exactly the stage height — so when the
    // content grew, it had nowhere to go and was clipped instead.
    alignItems: 'center',
    // Clears the notch and the home indicator on a handset, and the window
    // edges everywhere else.
    padding: 'max(26px, env(safe-area-inset-top)) 24px max(22px, env(safe-area-inset-bottom))',
  },

  // Lifted above the grain, which is a positioned pseudo-element and would
  // otherwise paint over static content. See `.grain` in styles/global.css.
  column: {
    position: 'relative',
    zIndex: 1,
    width: '100%',
    maxWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    // Fills the stage when there is room, and never shrinks below its own
    // content when there isn't — which is what lets the stage grow and the
    // wrapper scroll on a short handset.
    flex: '1 0 auto',
  },

  head: { flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: 6 },

  // Centred in whatever is left between the wordmark and the form. `1 0 auto`
  // rather than `1` with `minHeight: 0`: the old pair let this be squeezed
  // below its content, which is how the demo ended up clipped rather than
  // pushing the page taller.
  main: {
    flex: '1 0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '24px 0 28px',
  },

  foot: { flexShrink: 0 },

  legal: { margin: '18px 0 0', fontSize: 12, textAlign: 'center' },
  legalLink: { color: 'rgba(247,244,237,0.72)', textDecoration: 'none' },
  dot: { margin: '0 8px', color: 'rgba(247,244,237,0.38)' },
};
